/**
 * Canonical app-store links + attribution for the web app.
 *
 * One place for the App Store / Play Store / Chrome Web Store URLs so we
 * never drift store IDs across the codebase again. Every store link built
 * here carries UTM params (utm_source=web_app&utm_medium=<placement>&
 * utm_campaign=app_install) and, for Play, the Install Referrer mirror so
 * the campaign survives into the Play Console install report.
 *
 * Apple ignores utm_* entirely. The only thing App Store Connect counts is a
 * campaign link (`pt` provider token + `ct` campaign token + `mt=8`), which
 * shows up under App Analytics > Acquisition > Campaigns with product page
 * views, downloads and proceeds per `ct`. The landing site has carried these
 * since day one; the web app did not, so every install it drove was invisible
 * there. `ct` is `web_app-<placement>`, at most 30 characters.
 *
 * On a phone, the store badge for that phone's own store goes through the
 * site's app hand-off route instead (HANDOFF_URL below): it opens baba when it
 * is already installed, and its server-side `app_store_redirect` event puts
 * web-app taps in the same report as the website's. Desktop and the other
 * phone's badge keep the direct store URL, because the route picks the store
 * from the device and would send them to a QR page or the wrong store.
 *
 * Pair this with trackAppStoreClick() in lib/analytics.ts on every click.
 */

export type StorePlatform = 'ios' | 'android'
export type DevicePlatform = StorePlatform | 'desktop'

/** Canonical store identifiers — the single source of truth. */
export const APP_STORE_ID = '6740703683'
export const PLAY_PACKAGE = 'com.itsbaba.baba'
export const CHROME_EXTENSION_ID = 'gfkpgdbomjdknggfnigimodaeaakbchk'
/** App Store Connect provider token (public, same one the landing site uses). */
export const APPLE_CAMPAIGN_PROVIDER_TOKEN = '128369664'

/** Bare canonical URLs (no attribution) — handy for metadata/SEO. */
export const APP_STORE_URL = `https://apps.apple.com/us/app/baba-hebrew-translator/id${APP_STORE_ID}`
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${PLAY_PACKAGE}`
export const CHROME_STORE_URL = `https://chromewebstore.google.com/detail/baba-hebrew-translator/${CHROME_EXTENSION_ID}`

/** Canonical app rating used across surfaces. */
export const RATINGS = {
  ios: { score: 4.8, count: 1200 },
  android: { score: 4.8, count: 1200 },
  /** Combined, for when one number reads better than two. */
  combined: { score: 4.8, count: 1200 },
} as const

/** The website's hand-off route (apps/landing/lib/app-handoff.ts); `src` names this app. */
export const HANDOFF_URL = 'https://itsbaba.com/app/translate'
const HANDOFF_SOURCE = 'webapp'

const UTM_SOURCE = 'web_app'
const UTM_CAMPAIGN = 'app_install'

/** App Store Connect accepts campaign tokens of up to 30 characters. */
export function appleCampaignToken(placement: string): string {
  const cleaned = placement.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '')
  return `${UTM_SOURCE}-${cleaned || 'link'}`.slice(0, 30).replace(/-+$/g, '')
}

/** The hand-off link for a placement: `pl` takes the same role utm_medium has below. */
export function getHandoffUrl(placement: string): string {
  const pl = placement.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^[-_]+|[-_]+$/g, '').slice(0, 48)
  const params = new URLSearchParams({ src: HANDOFF_SOURCE, pl: pl || 'link' })
  return `${HANDOFF_URL}?${params.toString()}`
}

/**
 * Build the attributed store URL for a platform. `placement` becomes
 * utm_medium so we can tell which surface drove the install (e.g.
 * 'translator', 'onboarding', 'paywall').
 *
 * `device` is the visitor's own device; it defaults to the browser's user
 * agent. Server code (the edge mobile gate) must pass it, since a Worker's
 * navigator is not the visitor's. When it matches `platform`, the link is the
 * hand-off route; otherwise the direct store URL.
 */
export function getStoreUrl(
  platform: StorePlatform,
  placement: string,
  device: DevicePlatform = detectPlatform(),
): string {
  if (device === platform) return getHandoffUrl(placement)
  if (platform === 'ios') {
    const params = new URLSearchParams({
      utm_source: UTM_SOURCE,
      utm_medium: placement,
      utm_campaign: UTM_CAMPAIGN,
      pt: APPLE_CAMPAIGN_PROVIDER_TOKEN,
      ct: appleCampaignToken(placement),
      mt: '8',
    })
    return `${APP_STORE_URL}?${params.toString()}`
  }
  // Play Install Referrer wants the campaign mirrored into `referrer` so it
  // shows up in the Play Console — plain UTMs alone do not survive the store.
  // The mobile app reads this string on first launch and records it as the
  // install's acquisition source, so the placement has to be in here too.
  const referrer = `utm_source=${UTM_SOURCE}&utm_medium=${placement}&utm_campaign=${UTM_CAMPAIGN}`
  const params = new URLSearchParams({
    id: PLAY_PACKAGE,
    utm_source: UTM_SOURCE,
    utm_medium: placement,
    utm_campaign: UTM_CAMPAIGN,
    referrer,
  })
  return `https://play.google.com/store/apps/details?${params.toString()}`
}

/** Attributed Chrome Web Store URL. */
export function getChromeStoreUrl(placement: string): string {
  const params = new URLSearchParams({
    utm_source: UTM_SOURCE,
    utm_medium: placement,
    utm_campaign: UTM_CAMPAIGN,
  })
  return `${CHROME_STORE_URL}?${params.toString()}`
}

/**
 * Best-effort device detection from the user agent. iPhone/iPad/iPod → ios,
 * Android → android, everything else (incl. SSR) → desktop.
 */
export function detectPlatform(): DevicePlatform {
  if (typeof navigator === 'undefined') return 'desktop'
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'desktop'
}
